import { ListRange } from '@angular/cdk/collections';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AnyAbility } from '@casl/ability';
import { AblePurePipe } from '@casl/angular';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { FetchAbsenceCategoriesIfNeededState } from '@wilson/absences';
import { AuthState } from '@wilson/auth/core';
import { FeaturePurePipe } from '@wilson/authorization';
import { FeatureFlagPurePipe } from '@wilson/feature-flags';
import {
  FeatureName,
  PreferredSetting,
  RoleAction,
  RolePermissionSubject,
  ROW_BORDER_BOTTOM_PX,
  TimelineTimeframe,
  UserTimelines,
  ZOOM_LEVEL,
} from '@wilson/interfaces';
import { toDatabaseDateFormat } from '@wilson/localization';
import { AbsenceDrawerHelperService } from '@wilson/non-domain-specific/absence';
import { StaysDrawerStateService } from '@wilson/non-domain-specific/stays-form-and-drawer';
import { ShiftTimelinePreferredSettingsState } from '@wilson/preferred-settings/state';
import {
  getListRangeAt,
  ShiftsIconRenderingHelperService,
  WeekendHolidayHighlighterService,
} from '@wilson/shift-timeline/assignments-gantt-chart';
import {
  CreateNewViaDragAndDropService,
  DragAndDropModeService,
} from '@wilson/shift-timeline/services';
import {
  AssignedRegionVisibilityStateService,
  FetchActivitiesForUnassignedShifts,
  HighlightSelectedServices,
  HighlightSelectedShifts,
  PinUsersOnTimeline,
  SetMainTimelineHeightPx,
  ShiftTimelineDataState,
  ShiftTimelineFilterStateService,
  ShiftTimelineSettingsState,
  ShiftTimelineState,
  ShiftTimelineUnassignedRegionState,
  TimelineAssignedShiftValidationsStateService,
  TimelineFetchUsers,
  TimelineFetchUsersOvertimes,
  TimelineLoadActivitiesForShifts,
  TimelineSetCurrentDate,
  TimelineStaysSuggestionService,
  TimelineUnassignedShiftValidationsStateService,
  UnassignedRegionVisibleShiftsStateService,
} from '@wilson/shift-timeline/state';
import { SideNavSettingsState } from '@wilson/side-nav-settings/state';
import { isValid } from 'date-fns';
import { NzMessageRef, NzMessageService } from 'ng-zorro-antd/message';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  first,
  firstValueFrom,
  from,
  map,
  Observable,
  of,
  shareReplay,
  Subscription,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { PinUsersYoTopPipe } from '../../pipes/pin-users-to-top.pipe';
import { SortUsersByPreferredSettings } from '../../pipes/sort-users-by-preferred-settings.pipe';
import { DragAndDropHelperService } from '../../services/drag-and-drop-helper.service';
import { MouseMovementHelperService } from '../../services/mouse-movement-helper.service';
import { TimelineCommonOperationsService } from '../../services/timeline-common-operations.service';
import { MultipleShiftsDrawerService } from '../drawers/multiple-shifts-drawer/multiple-shifts-drawer.service';
import { ShiftTimelineService } from '../shift-timeline/shift-timeline.service';
import { HorizontalScrollingManagerService } from './horizontal-scrolling-manager.service';

@Component({
  selector: 'wilson-shift-timeline-gantt-chart',
  templateUrl: './shift-timeline-gantt-chart.component.html',
  styleUrls: ['./shift-timeline-gantt-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    MultipleShiftsDrawerService,
    StaysDrawerStateService,
    SortUsersByPreferredSettings,
    PinUsersYoTopPipe,
  ],
  standalone: false,
})
export class ShiftTimelineGanttChartComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private readonly store = inject(Store);
  private readonly timelineCommonOperationsService = inject(
    TimelineCommonOperationsService,
  );
  private nzMessageService = inject(NzMessageService);
  private readonly translateService = inject(TranslateService);
  protected timelineStaysSuggestionService = inject(
    TimelineStaysSuggestionService,
  );
  private readonly dragAndDropModeService = inject(DragAndDropModeService);
  private shiftTimelineFilterStateService = inject(
    ShiftTimelineFilterStateService,
  );
  private assignedRegionVisibilityStateService = inject(
    AssignedRegionVisibilityStateService,
  );
  private unassignedRegionVisibleShiftsStateService = inject(
    UnassignedRegionVisibleShiftsStateService,
  );
  private horizontalScrollingManagerService = inject(
    HorizontalScrollingManagerService,
  );
  private weekendHolidayHighlighterService = inject(
    WeekendHolidayHighlighterService,
  );
  protected readonly mouseMovementHelperService = inject(
    MouseMovementHelperService,
  );
  protected readonly dragAndDropHelperService = inject(
    DragAndDropHelperService,
  );
  private readonly featureFlagPurePipe = inject(FeatureFlagPurePipe);
  private readonly featurePurePipe = inject(FeaturePurePipe);
  private readonly ablePurePipe =
    inject<AblePurePipe<AnyAbility>>(AblePurePipe);
  private shiftTimelineService = inject(ShiftTimelineService);
  private readonly timelineAssignedShiftValidationsStateService = inject(
    TimelineAssignedShiftValidationsStateService,
  );
  private readonly timelineUnassignedShiftValidationsStateService = inject(
    TimelineUnassignedShiftValidationsStateService,
  );
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly shiftsIconRenderingHelperService = inject(
    ShiftsIconRenderingHelperService,
  );
  private readonly sortUsersByPreferredSettings = inject(
    SortUsersByPreferredSettings,
  );
  private readonly pinUsersYoTopPipe = inject(PinUsersYoTopPipe);
  private readonly activatedRoute = inject(ActivatedRoute);
  protected readonly absenceDrawerHelperService = inject(
    AbsenceDrawerHelperService,
  );

  public userTimelinesInfo$!: Observable<UserTimelines[]>;
  public isRefreshing$!: Observable<boolean>;
  public maxUnassignedRowHeightPx$!: Observable<number>;
  public zoomLevel$ = this.store.select(ShiftTimelineSettingsState.zoomLevel);
  public isCellView$!: Observable<boolean>;

  public defaultRowHeight$ = this.store.select(
    ShiftTimelineSettingsState.defaultRowHeight,
  );
  private loadingMessageRef: NzMessageRef | null = null;
  private subscriptions = new Subscription();
  private visibleUserIdsSet$!: Observable<Set<string>>;

  @ViewChildren('rowsContainerVirtualScroll')
  protected rowsContainerVirtualScroll!: QueryList<CdkVirtualScrollViewport>;
  protected isForcingRerender$ = this.shiftTimelineService.forceRerendering$;
  protected stickyHasShadow = false;
  protected isLoadingData$!: Observable<boolean>;
  protected userPreferredSettings$!: Observable<PreferredSetting>;
  protected currentScrollLeftPositionSubject = new BehaviorSubject(0);
  protected ZOOM_LEVEL = ZOOM_LEVEL;
  protected isCollapsed$!: Observable<boolean>;
  protected scrollDebouncerSubject = new BehaviorSubject<{
    viewPort: CdkVirtualScrollViewport;
    totalItems: number | null | undefined;
  } | null>(null);

  protected isFilterDrawerVisible$ =
    this.shiftTimelineFilterStateService.showFilterDrawer$;

  protected resourcesColumnWidthPx$ = this.store.select(
    ShiftTimelineSettingsState.resourcesColumnWidthPx,
  );
  protected areActivitiesVisible$ = this.store.select(
    ShiftTimelineSettingsState.areActivitiesVisible,
  );

  protected isUnassignedRegionExpanded$ = this.store.select(
    ShiftTimelineUnassignedRegionState.isUnassignedRegionExpanded,
  );

  protected weekendHolidayRenderData$ =
    this.weekendHolidayHighlighterService.weekendHolidayRenderDataStream({
      visibleDaysRange$: this.store.select(
        ShiftTimelineDataState.visibleDateRange,
      ),
      holidayRegions$: this.store.select(AuthState.publicHolidaysRegion),
      frameLength$: this.store.select(ShiftTimelineState.frameLength),
      timeframe$: this.store.select(ShiftTimelineDataState.timeframe),
    });

  protected previousRowHeight: number | null = null;

  protected filledItemHeight$: Observable<number[]> = of([]);
  protected viewPort$ = new BehaviorSubject<CdkVirtualScrollViewport | null>(
    null,
  );
  protected filteredViewPort$: Observable<CdkVirtualScrollViewport> =
    this.viewPort$.pipe(
      filter((viewport) => !!viewport),
      debounceTime(10),
    );
  protected theoreticalListRange$: Observable<ListRange> = of({
    start: 0,
    end: 25,
  });
  protected sortedUserTimelinesInfo$!: Observable<UserTimelines[]>;
  protected rowHeightWithBorder$!: Observable<number>;
  protected isFirstScrollEvent = true;

  private itemHeights$!: Observable<number[]>;
  private timeoutIdSet = new Set();

  private onScroll({
    leftOffset,
    rightOffset,
  }: {
    leftOffset: number;
    rightOffset: number;
  }): void {
    this.shiftsIconRenderingHelperService.resetStateForTimeline();
    this.shiftsIconRenderingHelperService.resetStateForUnassignedRegion();
    this.horizontalScrollingManagerService.updateScrollTrackingData({
      leftOffsetPx: leftOffset,
      rightOffsetPx: rightOffset,
    });
  }

  async ngOnInit(): Promise<void> {
    this.activatedRoute.queryParams.pipe(take(1)).subscribe((params) => {
      if (params['noValidation']?.toString() === 'true') {
        console.debug('No validation detected. Turning off validation queue');
      } else {
        this.timelineAssignedShiftValidationsStateService.turnOnValidationQueue();
        this.timelineUnassignedShiftValidationsStateService.turnOnValidationQueue();
      }
    });

    this.store.dispatch(new FetchAbsenceCategoriesIfNeededState());

    const userId = await firstValueFrom(this.store.select(AuthState.userId));
    if (userId) {
      // Wait until cached timeline filters are merged into the URL (async Router.navigate).
      // Otherwise later code can read a stale query snapshot and we see flaky restore / cache behavior.
      // Other features also merge query params (e.g. KPI / layout option modal).
      // MR: https://gitlab.com/menlo79/wilson/-/merge_requests/5994
      // Ticket: https://menlo1679.atlassian.net/browse/WILP-10054
      await this.shiftTimelineFilterStateService.setFilterSettings(userId);
    }

    const userId$ = this.store.select(AuthState.userId).pipe(
      filter((userId) => userId !== null),
      shareReplay(1),
    );

    this.isCellView$ = userId$.pipe(
      switchMap((userId) =>
        this.store.select(
          ShiftTimelinePreferredSettingsState.isCellView(userId),
        ),
      ),
      tap(() => {
        // Resetting scroll state when a new view port is loaded to prevent stale state, which can lead to loading data from the wrong range
        this.horizontalScrollingManagerService.resetScrollTrackingData();
        this.cdr.detectChanges();
      }),
    );

    this.isCollapsed$ = userId$.pipe(
      switchMap((userId) =>
        this.store.select(SideNavSettingsState.getIsSideNavCollapsed(userId)),
      ),
    );

    this.visibleUserIdsSet$ = combineLatest([
      this.assignedRegionVisibilityStateService.visibleUserIdsSet$.pipe(
        debounceTime(50),
      ),
      this.store
        .select(ShiftTimelineDataState.visibleDateRange)
        .pipe(debounceTime(50)),
      this.isCellView$,
    ]).pipe(
      distinctUntilChanged(
        (
          [oldVisibleUserIdsSet, oldVisibleDateRange],
          [newVisibleUserIdsSet, newVisibleDateRange, isCellView],
        ) => {
          if (
            // In cell view, there are not variable row heights so we do not need to compare to the old values
            !isCellView &&
            oldVisibleDateRange.start === newVisibleDateRange.start &&
            oldVisibleDateRange.end === newVisibleDateRange.end
          ) {
            if (
              oldVisibleUserIdsSet.size === 0 &&
              newVisibleUserIdsSet.size > 0
            ) {
              return false;
            } else {
              for (const value of newVisibleUserIdsSet) {
                if (!oldVisibleUserIdsSet.has(value)) {
                  return false; // Emit new event and stop further checks
                }
              }

              return true;
            }
          } else {
            return false;
          }
        },
      ),
      map(([visibleUserIdsSet]) => {
        return visibleUserIdsSet;
      }),
      debounceTime(50),
    );

    this.subscriptions.add(
      this.scrollDebouncerSubject.pipe(debounceTime(10)).subscribe((data) => {
        if (data) {
          const leftOffset = data.viewPort.measureScrollOffset('left');
          const rightOffset = data.viewPort.measureScrollOffset('right');

          this.onScroll({
            leftOffset,
            rightOffset,
          });
          this.setScrollHorizontalPosition(leftOffset);
        }
      }),
    );

    this.subscriptions.add(
      this.timelineCommonOperationsService.updateScrollLeftPosition$.subscribe(
        () => {
          this.currentScrollLeftPositionSubject.next(0);
        },
      ),
    );

    this.subscriptions.add(
      this.timelineCommonOperationsService.updatedScrollLeftPositionOnResize$.subscribe(
        (scrollLeftPx) => {
          this.currentScrollLeftPositionSubject.next(scrollLeftPx);
        },
      ),
    );

    this.store.dispatch([
      new TimelineFetchUsers(),
      new TimelineFetchUsersOvertimes(),
    ]);
    await firstValueFrom(
      this.timelineCommonOperationsService.setPreferredSetting(),
    );

    this.isRefreshing$ = combineLatest([
      this.store.select(
        ShiftTimelineUnassignedRegionState.isRefreshingUnassignedShifts,
      ),
      this.store.select(ShiftTimelineDataState.isRefreshingUsers),
    ]).pipe(
      map(
        ([isRefreshingUnassignedShifts, isRefreshingUserTimeline]: [
          boolean,
          boolean,
        ]) => {
          return isRefreshingUnassignedShifts && isRefreshingUserTimeline;
        },
      ),
    );

    this.userTimelinesInfo$ =
      this.shiftTimelineFilterStateService.filteredUsers$.pipe(
        map((userTimelinesInfo) => {
          return userTimelinesInfo.map((userTimeline) => {
            const user = { ...userTimeline };
            user.determinedRowDataV2 =
              this.store.selectSnapshot(
                ShiftTimelineDataState.userTimelineRowDataStreamV2(
                  user.id as string,
                ),
              ) || [];
            return user;
          });
        }),
        distinctUntilChanged(
          (previousValue: UserTimelines[], currentValue: UserTimelines[]) => {
            if (previousValue.length !== currentValue.length) {
              return false;
            } else {
              const prevIds = previousValue.map((user) => user.id);
              return currentValue.every(
                (user, index) => user.id === prevIds[index],
              );
            }
          },
        ),
      );

    this.rowHeightWithBorder$ = this.defaultRowHeight$.pipe(
      map((rowHeight) => rowHeight + ROW_BORDER_BOTTOM_PX),
    );

    this.itemHeights$ = combineLatest([
      this.userTimelinesInfo$,
      this.rowHeightWithBorder$,
    ]).pipe(
      map(([userTimelinesInfo, rowHeightWithBorder]) => {
        return Array(userTimelinesInfo.length).fill(rowHeightWithBorder);
      }),
    );

    this.theoreticalListRange$ = combineLatest([
      this.itemHeights$,
      this.filteredViewPort$,
    ]).pipe(
      debounceTime(10),
      map(([filledItemHeight, viewport]) => {
        const range = getListRangeAt({
          itemHeights: filledItemHeight,
          scrollOffset: viewport.measureScrollOffset('top'),
          viewportSize: viewport.getViewportSize(),
          bufferBefore: 2,
          bufferAfter: 2,
        });

        this.stickyHasShadow = range.start === 0;
        return range;
      }),
      shareReplay(1),
    );

    this.userPreferredSettings$ = this.store.select(AuthState.userId).pipe(
      filter((userId): userId is string => typeof userId === 'string'),
      switchMap((userId) =>
        this.store.select(
          ShiftTimelinePreferredSettingsState.getPreferredSetting(userId),
        ),
      ),
    );

    this.sortedUserTimelinesInfo$ = combineLatest([
      this.userTimelinesInfo$,
      this.userPreferredSettings$.pipe(
        filter(
          (userPreferredSettings): userPreferredSettings is PreferredSetting =>
            userPreferredSettings !== null,
        ),
      ),
    ]).pipe(
      map(([userTimelinesInfo, userPreferredSettings]) => {
        const sortedUsers = this.sortUsersByPreferredSettings.transform(
          userTimelinesInfo,
          userPreferredSettings,
        );
        return this.pinUsersYoTopPipe.transform(sortedUsers);
      }),
      shareReplay(1),
    );

    this.isLoadingData$ = this.store.select(
      ShiftTimelineDataState.isLoadingUsers,
    );

    this.subscriptions.add(
      combineLatest([
        this.theoreticalListRange$,
        this.sortedUserTimelinesInfo$,
      ]).subscribe(([range, sortedUserTimelinesInfo]) => {
        this.assignedRegionVisibilityStateService.updateUserVisibility(
          new Set(
            sortedUserTimelinesInfo
              .slice(range.start, range.end)
              .map((user) => user.id),
          ),
        );
      }),
    );

    this.subscriptions.add(
      this.shiftTimelineFilterStateService
        .filterCachingEventListener()
        .subscribe(),
    );

    this.subscriptions.add(
      this.store
        .select(ShiftTimelineDataState.hasSuccessfullyFetchedUsers)
        .pipe(
          filter((hasSuccessfullyFetchedUsers) => hasSuccessfullyFetchedUsers),
          switchMap(() => {
            return combineLatest([
              this.store.select(ShiftTimelineDataState.visibleDateRange),
              this.visibleUserIdsSet$,
              this.shiftTimelineFilterStateService.filters$,
            ]).pipe(
              debounceTime(50),
              distinctUntilChanged(
                (
                  [
                    previousVisibleDateRange,
                    previousVisibleUserIdsSet,
                    previousFilters,
                  ],
                  [newVisibleDateRange, newVisibleUserIdsSet, newFilters],
                ) => {
                  const isDateRangesAndFiltersSame =
                    this.timelineCommonOperationsService.areDateRangesAndFiltersSame(
                      [previousVisibleDateRange, previousFilters],
                      [newVisibleDateRange, newFilters],
                    );

                  const isVisibleUserIdsSetSame =
                    previousVisibleUserIdsSet.size ===
                      newVisibleUserIdsSet.size &&
                    Array.from(previousVisibleUserIdsSet).every((userId) =>
                      newVisibleUserIdsSet.has(userId),
                    );

                  return isDateRangesAndFiltersSame && isVisibleUserIdsSetSame;
                },
              ),
            );
          }),
        )
        .subscribe(async ([visibleDateRange, visibleUserIdsSet]) => {
          const draggingItemId = await firstValueFrom(
            this.dragAndDropModeService.draggingItemId$,
          );

          if (
            /**
             * We only fetch new data when dragging is not happening. This is necessary because
             * the list of visible users can change as overlapping items can cause changes to the height of each row.
             *
             * If we don't do this, we can receive data that would be outdated the moment the user drop the dragged item
             * This will lead to incorrect data in the state.
             */
            !draggingItemId &&
            visibleUserIdsSet.size > 0 &&
            isValid(visibleDateRange.start) &&
            isValid(visibleDateRange.end)
          ) {
            this.timelineCommonOperationsService.updateUsersAssignmentsState(
              Array.from(visibleUserIdsSet),
            );
            this.timelineCommonOperationsService.refreshTotalProductivityAllFilteredUsers(
              true,
            );
            this.timelineCommonOperationsService.refreshKpisForVisibleUsers(
              true,
            );
          }
        }),
    );

    this.subscriptions.add(
      combineLatest([
        this.store.select(ShiftTimelineDataState.loadingActivitiesForShiftIds),
        this.store.select(
          ShiftTimelineDataState.usersWithUpdatingShiftAssignments,
        ),
        this.store.select(ShiftTimelineDataState.isLoadingFilteredUsers),
      ])
        .pipe(debounceTime(200))
        .subscribe(
          ([
            loadingActivitiesForShiftIds,
            usersWithUpdatingShiftAssignments,
            isLoadingFilteredUsers,
          ]) => {
            if (
              (loadingActivitiesForShiftIds.length > 0 ||
                usersWithUpdatingShiftAssignments.length > 0 ||
                isLoadingFilteredUsers) &&
              !this.loadingMessageRef
            ) {
              this.loadingMessageRef = this.nzMessageService.loading(
                this.translateService.instant('general.loading'),
              );
            } else {
              this.nzMessageService.remove(this.loadingMessageRef?.messageId);
              this.loadingMessageRef = null;
            }
          },
        ),
    );

    this.subscriptions.add(
      this.assignedRegionVisibilityStateService.visibleShiftIdsSet$
        .pipe(debounceTime(150))
        .subscribe((visibleShiftIdsSet) => {
          this.timelineAssignedShiftValidationsStateService.keepOnlyVisibleShiftsValidations(
            Array.from(visibleShiftIdsSet),
          );
        }),
    );

    this.subscriptions.add(
      this.store
        .select(ShiftTimelineSettingsState.areActivitiesVisible)
        .pipe(
          filter((areActivitiesVisible) => areActivitiesVisible),
          switchMap(() => {
            return combineLatest([
              this.unassignedRegionVisibleShiftsStateService
                .visibleShiftIdsSet$,
              this.assignedRegionVisibilityStateService.visibleShiftIdsSet$,
            ]).pipe(take(1));
          }),
        )
        .pipe(debounceTime(150))
        .subscribe(
          ([unassignedVisibleShiftIdsSet, assignedVisibleShiftIdsSet]) => {
            if (unassignedVisibleShiftIdsSet.size > 0) {
              this.store.dispatch(
                new FetchActivitiesForUnassignedShifts(
                  Array.from(unassignedVisibleShiftIdsSet),
                ),
              );
            }
            if (assignedVisibleShiftIdsSet.size > 0) {
              this.store.dispatch(
                new TimelineLoadActivitiesForShifts(assignedVisibleShiftIdsSet),
              );
            }
          },
        ),
    );

    this.subscriptions.add(
      combineLatest([
        // We purposely do not debounce here because we need to react to every change, otherwise the changes in the middle will be thrown away
        this.assignedRegionVisibilityStateService.newlyVisibleShiftIdsSet$,
        this.store
          .select(ShiftTimelineSettingsState.areActivitiesVisible)
          .pipe(filter((areActivitiesVisible) => areActivitiesVisible)),
      ]).subscribe(([{ new: newlyVisibleShiftIdsSet }]) => {
        const timeoutId = setTimeout(() => {
          let assignedShiftIds = Array.from(newlyVisibleShiftIdsSet);
          if (
            newlyVisibleShiftIdsSet.has(
              CreateNewViaDragAndDropService.MOCK_SHIFT_ID,
            )
          ) {
            assignedShiftIds = assignedShiftIds.filter(
              (shiftId) =>
                shiftId !== CreateNewViaDragAndDropService.MOCK_SHIFT_ID,
            );
          }

          if (assignedShiftIds.length > 0) {
            const uniqueShiftIdsSet = new Set(assignedShiftIds);
            this.store.dispatch(
              new TimelineLoadActivitiesForShifts(uniqueShiftIdsSet),
            );
          }
          this.timeoutIdSet.delete(timeoutId);
        });
        this.timeoutIdSet.add(timeoutId);
      }),
    );

    this.subscriptions.add(
      this.unassignedRegionVisibleShiftsStateService.visibleShiftIdsSet$
        .pipe(debounceTime(150))
        .subscribe((visibleShiftIdsSet) => {
          this.timelineUnassignedShiftValidationsStateService.keepOnlyVisibleShiftsValidations(
            Array.from(visibleShiftIdsSet),
          );
        }),
    );

    this.subscriptions.add(
      combineLatest([
        // We purposely do not debounce here because we need to react to every change, otherwise the changes in the middle will be thrown away
        this.unassignedRegionVisibleShiftsStateService.newlyVisibleShiftIdsSet$,
        this.store
          .select(ShiftTimelineSettingsState.areActivitiesVisible)
          .pipe(filter((areActivitiesVisible) => areActivitiesVisible)),
      ]).subscribe(([{ new: newlyVisibleShiftIdsSet }]) => {
        const timeoutId = setTimeout(() => {
          this.store.dispatch(
            new FetchActivitiesForUnassignedShifts(
              Array.from(newlyVisibleShiftIdsSet),
            ),
          );
          this.timeoutIdSet.delete(timeoutId);
        });
        this.timeoutIdSet.add(timeoutId);
      }),
    );

    this.subscriptions.add(
      combineLatest([
        this.featureFlagPurePipe.transform(
          'portal-validation-without-assignments',
        ),
        // We purposely do not debounce here because we need to react to every change, otherwise the changes in the middle will be thrown away
        this.unassignedRegionVisibleShiftsStateService.newlyVisibleShiftIdsSet$,
      ]).subscribe(
        ([
          isValidationWithoutAssignmentsEnabled,
          { new: newlyVisibleShiftIdsSet, previouslyVisibleShiftIds },
        ]) => {
          const timeoutId = setTimeout(() => {
            const unassignedShiftIds: string[] = Array.from(
              newlyVisibleShiftIdsSet,
            );
            if (
              unassignedShiftIds.length > 0 &&
              isValidationWithoutAssignmentsEnabled
            ) {
              this.timelineUnassignedShiftValidationsStateService.queueValidationRequest(
                unassignedShiftIds,
                {
                  forceRefresh: true,
                  previouslyVisibleShiftIds: Array.from(
                    previouslyVisibleShiftIds,
                  ),
                },
              );
            }
            this.timeoutIdSet.delete(timeoutId);
          });
          this.timeoutIdSet.add(timeoutId);
        },
      ),
    );

    this.subscriptions.add(
      from(
        // We purposely do not debounce here because we need to react to every change, otherwise the changes in the middle will be thrown away
        this.assignedRegionVisibilityStateService.newlyVisibleShiftIdsSet$,
      ).subscribe(
        ({ new: newlyVisibleShiftIdsSet, previouslyVisibleShiftIds }) => {
          // setTimeout to move the work into a new macro task to improve the performance
          const timeoutId = setTimeout(() => {
            let assignedShiftIds = Array.from(newlyVisibleShiftIdsSet);
            if (
              newlyVisibleShiftIdsSet.has(
                CreateNewViaDragAndDropService.MOCK_SHIFT_ID,
              )
            ) {
              assignedShiftIds = assignedShiftIds.filter(
                (shiftId) =>
                  shiftId !== CreateNewViaDragAndDropService.MOCK_SHIFT_ID,
              );
            }

            if (assignedShiftIds.length > 0) {
              this.timelineAssignedShiftValidationsStateService.queueValidationRequest(
                assignedShiftIds,
                {
                  forceRefresh: true,
                  previouslyVisibleShiftIds: Array.from(
                    previouslyVisibleShiftIds,
                  ),
                },
              );
            }
            this.timeoutIdSet.delete(timeoutId);
          });
          this.timeoutIdSet.add(timeoutId);
        },
      ),
    );

    this.subscriptions.add(
      combineLatest([
        this.featureFlagPurePipe.transform('timeline-stays-management'),
        this.featurePurePipe.transform(FeatureName.StayManagement),
        this.ablePurePipe.transform(
          RoleAction.Read,
          RolePermissionSubject.Stay,
        ),
      ])
        .pipe(
          filter(
            ([
              isStaysManagementFeatureFlagEnabled,
              isStaysManagementFeatureEnabled,
              hasRoleToAccessStaysManagement,
            ]) =>
              isStaysManagementFeatureFlagEnabled &&
              isStaysManagementFeatureEnabled &&
              hasRoleToAccessStaysManagement,
          ),
          switchMap(() => {
            return combineLatest([
              this.store
                .select(ShiftTimelineDataState.visibleDateRange)
                .pipe(debounceTime(350)),
              this.visibleUserIdsSet$.pipe(debounceTime(450)),
              this.store.select(ShiftTimelineSettingsState.areStaysVisible),
            ]);
          }),
        )
        .subscribe(
          async ([visibleDateRange, visibleUserIdsSet, areStaysVisible]) => {
            const draggingItemId = await firstValueFrom(
              this.dragAndDropModeService.draggingItemId$,
            );
            if (
              !draggingItemId &&
              visibleUserIdsSet.size > 0 &&
              isValid(visibleDateRange.start) &&
              isValid(visibleDateRange.end) &&
              areStaysVisible
            ) {
              this.handleStaysFetch(
                visibleDateRange,
                Array.from(visibleUserIdsSet),
              );
            }
          },
        ),
    );

    const millisecondsToRemoveHighlighting = 4000;
    setTimeout(() => {
      this.store.dispatch(new HighlightSelectedShifts([]));
      this.store.dispatch(new HighlightSelectedServices([]));
      this.store.dispatch(new PinUsersOnTimeline([]));
    }, millisecondsToRemoveHighlighting);
  }

  private handleStaysFetch(
    visibleDateRange: TimelineTimeframe,
    visibleUserIds: string[],
  ): void {
    firstValueFrom(
      this.timelineStaysSuggestionService.fetchNecessaryStaysInRange({
        userIds: visibleUserIds,
        dateRange: visibleDateRange,
      }),
    );
    firstValueFrom(
      this.timelineStaysSuggestionService.fetchValidateAndStoreUserStays({
        userIds: visibleUserIds,
        dateRange: visibleDateRange,
      }),
    );
  }

  ngAfterViewInit(): void {
    const rowContainerRef$ = this.rowsContainerVirtualScroll.changes.pipe(
      map(() => this.rowsContainerVirtualScroll.get(0)),
      filter((container) => !!container),
    );

    this.subscriptions.add(
      this.horizontalScrollingManagerService.subscribeToDateRangeUpdater(
        rowContainerRef$,
      ),
    );

    const setHeightSub = rowContainerRef$
      .pipe(
        filter((rowContainer) => !!rowContainer),
        switchMap((rowContainer) =>
          combineLatest([
            of(rowContainer),
            rowContainer.scrolledIndexChange,
            this.store.select(ShiftTimelineSettingsState.windowHeight),
          ]),
        ),
      )
      .subscribe(([viewPort]) => {
        this.viewPort$.next(viewPort);
        const currentViewPortHeight =
          viewPort.getElementRef().nativeElement.clientHeight;
        if (
          this.store.selectSnapshot(
            ShiftTimelineDataState.currentTimelineHeightPx,
          ) !== currentViewPortHeight
        ) {
          this.store.dispatch(
            new SetMainTimelineHeightPx({
              heightPx: currentViewPortHeight,
            }),
          );
        }
      });

    this.subscriptions.add(setHeightSub);

    const scrollSyncListener = combineLatest([
      rowContainerRef$,
      this.currentScrollLeftPositionSubject,
    ]).subscribe(
      ([viewPort, scrollLeftPx]: [
        CdkVirtualScrollViewport | undefined,
        number,
      ]) => {
        viewPort?.scrollTo({
          left: scrollLeftPx,
        });
      },
    );
    this.subscriptions.add(scrollSyncListener);

    // On first scroll event, we manually trigger load data for more days to the left to set the scroll bar in the middle
    this.filteredViewPort$
      .pipe(first((viewport) => !!viewport))
      .subscribe((viewport) => {
        if (this.isFirstScrollEvent && viewport) {
          this.isFirstScrollEvent = false;
          this.horizontalScrollingManagerService.loadDataForMoreDaysToLeft(
            viewport,
          );
        }
      });
  }

  setScrollHorizontalPosition(scrollLeftPx: number): void {
    if (this.currentScrollLeftPositionSubject.value !== scrollLeftPx) {
      this.currentScrollLeftPositionSubject.next(scrollLeftPx);
    }
    this.updateTimelineDateInURL(scrollLeftPx);
  }

  private updateTimelineDateInURL(scrollLeftPx: number): void {
    const timelineDate = this.dragAndDropHelperService.calculateTimeFromPx(
      scrollLeftPx,
      1,
    );

    const formattedDate = toDatabaseDateFormat(timelineDate);
    this.store.dispatch(new TimelineSetCurrentDate(formattedDate));
  }

  ngOnDestroy(): void {
    this.timelineAssignedShiftValidationsStateService.turnOffValidationQueue();
    this.timelineUnassignedShiftValidationsStateService.turnOffValidationQueue();
    this.subscriptions.unsubscribe();
    this.assignedRegionVisibilityStateService.clearAllShiftVisibilityRecord();
    this.timeoutIdSet.forEach((timeoutId) => {
      clearTimeout(timeoutId as number);
    });
    this.timeoutIdSet.clear();
  }
}
